import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, LogOut, Search, X } from "@fg/icons";
import { connectParityApi, type ChatListItem } from "@/api/connectApi";
import { useConversationMembers } from "@/api/hooks";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";
import { ChatAvatar } from "./chatUi";

interface DirectoryUser { userId: string; email: string; displayName: string | null; tenantId: string }

/**
 * Teams' group chat details: name the chat, see who is in it, add people (with or without the chat history) and remove people or
 * leave. Everyone in a group chat can do all of it, as in Teams; each change leaves a line in the chat.
 */
export function GroupManageDialog({ chat, me, onClose, onLeft }: {
    chat: ChatListItem;
    /** The signed-in user's normalised id. */
    me: string;
    onClose: () => void;
    onLeft: () => void;
}) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const members = useConversationMembers(chat.id);
    const [name, setName] = useState(chat.title === chat.memberNames.join(", ") ? "" : chat.title);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<DirectoryUser[]>([]);
    const [picked, setPicked] = useState<DirectoryUser[]>([]);
    const [history, setHistory] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const list = members.data ?? [];

    // Debounced directory search; people already in the chat are left out.
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); return; }
        let cancelled = false;
        const handle = window.setTimeout(() => {
            void connectParityApi.searchDirectory(q, token).then((r) => { if (!cancelled) setResults(r); }).catch((e: Error) => { if (!cancelled) setError(e.message); });
        }, 200);
        return () => { cancelled = true; window.clearTimeout(handle); };
    }, [query, token]);

    const refresh = async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ["chats"] }),
            qc.invalidateQueries({ queryKey: ["conversation-members", chat.id] }),
            qc.invalidateQueries({ queryKey: ["conversation-messages", chat.id] })
        ]);
    };
    const run = async (label: string, fn: () => Promise<unknown>) => {
        setBusy(label);
        setError(null);
        try { await fn(); await refresh(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
    };

    const inChat = new Set(list.map((m) => m.userId));
    const offered = results.filter((u) => !inChat.has(u.userId));
    const toggle = (u: DirectoryUser) => setPicked((p) => (p.some((x) => x.userId === u.userId) ? p.filter((x) => x.userId !== u.userId) : [...p, u]));
    const renamed = name.trim() !== (chat.title === chat.memberNames.join(", ") ? "" : chat.title);

    return (
        <div role="dialog" aria-label="Group chat details" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
            <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg border bg-card shadow-2xl" onKeyDown={(e) => { if (e.key === "Escape" && !busy) onClose(); }}>
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-base font-semibold">Group chat details</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
                </div>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-sm">
                    <div>
                        <label htmlFor="group-name" className="mb-1 block text-xs font-medium">Chat name</label>
                        <div className="flex gap-2">
                            <input id="group-name" value={name} maxLength={256} onChange={(e) => setName(e.target.value)} placeholder="Name this chat" className="min-w-0 flex-1 rounded-md border bg-background px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary" onKeyDown={(e) => { if (e.key === "Enter" && renamed) void run("rename", () => connectParityApi.renameConversation(chat.id, name.trim(), token)); }} />
                            <button type="button" disabled={!renamed || Boolean(busy)} onClick={() => void run("rename", () => connectParityApi.renameConversation(chat.id, name.trim(), token))} className="rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">Save</button>
                        </div>
                    </div>

                    <div>
                        <h3 className="mb-1 text-xs font-medium">People ({list.length})</h3>
                        {members.isPending ? (
                            <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</p>
                        ) : (
                            <ul className="divide-y rounded-md border" role="list">
                                {list.map((m) => {
                                    const mine = m.userId === me;
                                    const label = m.displayName ?? m.email;
                                    return (
                                        <li key={m.id} className="flex items-center gap-2 px-3 py-1.5">
                                            <ChatAvatar name={label} size="sm" />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-medium">{label}{mine ? " (you)" : ""}</span>
                                                {m.displayName ? <span className="block truncate text-xs text-muted-foreground">{m.email}</span> : null}
                                            </span>
                                            {mine ? (
                                                <button type="button" disabled={Boolean(busy)} onClick={() => void run("leave", async () => { await connectParityApi.removeConversationMember(chat.id, m.userId, token); onLeft(); })} className="flex items-center gap-1 rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50">
                                                    <LogOut className="h-3.5 w-3.5" /> Leave
                                                </button>
                                            ) : (
                                                <button type="button" disabled={Boolean(busy) || list.length <= 2} aria-label={`Remove ${label}`} title={list.length <= 2 ? "A group chat needs at least two people" : `Remove ${label}`} onClick={() => void run("remove", () => connectParityApi.removeConversationMember(chat.id, m.userId, token))} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive disabled:opacity-40">
                                                    <X className="h-4 w-4" />
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div>
                        <label htmlFor="group-add" className="mb-1 block text-xs font-medium">Add people</label>
                        <div className="relative mb-2">
                            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <input id="group-add" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Type a name or email" className="w-full rounded-md border bg-background py-1.5 pl-9 pr-3 focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        {picked.length > 0 ? (
                            <div className="mb-2 flex flex-wrap gap-1">
                                {picked.map((p) => (
                                    <span key={p.userId} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                        {p.displayName ?? p.email}
                                        <button type="button" onClick={() => toggle(p)} aria-label={`Do not add ${p.displayName ?? p.email}`} className="rounded-full hover:bg-primary/20"><X className="h-3 w-3" /></button>
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        {query.trim().length >= 2 ? (
                            <ul className="max-h-40 overflow-auto rounded-md border" role="list">
                                {offered.length === 0 ? <li className="p-3 text-xs text-muted-foreground">No one else matches.</li> : offered.map((u) => {
                                    const on = picked.some((p) => p.userId === u.userId);
                                    return (
                                        <li key={u.userId}>
                                            <button type="button" onClick={() => toggle(u)} className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent", on && "bg-primary/5")}>
                                                <input type="checkbox" checked={on} readOnly className="h-3.5 w-3.5" />
                                                <span className="min-w-0 flex-1 truncate">{u.displayName ?? u.email}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : null}
                        <label className="mt-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} /> Include the earlier messages for the people I add</label>
                        <button type="button" disabled={picked.length === 0 || Boolean(busy)} onClick={() => void run("add", async () => { await connectParityApi.addConversationMembersWithHistory(chat.id, picked.map((p) => ({ userId: p.userId, email: p.email, displayName: p.displayName ?? undefined })), history, token); setPicked([]); setQuery(""); })} className="mt-2 flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                            {busy === "add" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            Add{picked.length > 0 ? ` (${picked.length})` : ""}
                        </button>
                    </div>

                    {error ? <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p> : null}
                </div>
            </div>
        </div>
    );
}
